/** @format */

import React, { useState, useMemo, useCallback } from "react";
import { shallowEqual, useSelector } from "react-redux";
import { Space } from "antd";
import { BreadCrumb } from "../../../components";
import { ButtonDropdownExport } from "../../../components/Button";
import notifAlert from "../../../components/NotifAlert";

import InputSearch from "../../../components/InputSearch";
import { columnsAlokasi } from "./Component/ColumnAlokasi";
import { TableWithPagination } from "components/Table/TableWithPagination";
import { useAlokasiBos } from "hooks/HookBos/useAlokasiBos";
import { ModalAlokasiBos } from "./Component/ModalAlokasiBos";
import { useFormModal } from "hooks/useFormModal";
import { useDebounce } from "hooks/useDebounce";
import { safe } from "utils/helper";
import {
  handleExportExcelAlokasi,
  handleExportPdfAlokasi,
} from "./Component/ExportAlokasi";

const AlokasiSumberDanaBos = () => {
  const itemBreadcrumb = [
    { path: "/", breadcrumbName: "Home" },
    { path: "/", breadcrumbName: "Pencairan Dokumen BOS" },
    { path: "/", breadcrumbName: "Alokasi Sumber Dana BOS" },
  ];
  const { auth, provinsi, kabkota } = useSelector(
    (state: any) => ({
      auth: state.auth?.data || null,
      provinsi: state.store?.provinsi,
      kabkota: state.store?.kabkota,
    }),
    shallowEqual,
  );
  const [search, setSearch] = useState<any>(null);
  const debouncedSearch = useDebounce(search, 400);
  const { loading, alokasiSumberDanaBos, editAlokasiBos, deleteAlokasiBos } =
    useAlokasiBos(auth);
  const { open, initialData, openWithInitialData, closeModal } = useFormModal();

  const dataTable = useMemo(() => {
    if (!debouncedSearch) return alokasiSumberDanaBos;

    const val = debouncedSearch.toLowerCase();

    return alokasiSumberDanaBos.filter((item: any) => {
      return (
        safe(item.namaMadrasah).includes(val) ||
        safe(item.jumlahSiswa).includes(val) ||
        safe(item.nsm).includes(val)
      );
    });
  }, [debouncedSearch, alokasiSumberDanaBos]);

  const handleExportFile = useCallback(
    (type: "pdf" | "excel") => {
      if (!dataTable.length) {
        notifAlert({
          type: "warning",
          description: "Maaf data tidak tersedia!",
        });
        return;
      }

      const payload = {
        auth,
        provinsi,
        kabkota,
        dataMadrasah: dataTable,
      };

      if (type === "pdf") {
        handleExportPdfAlokasi(payload);
      }

      if (type === "excel") {
        handleExportExcelAlokasi(payload);
      }

      notifAlert({
        type: "success",
        description: "Data berhasil di export",
      });
    },
    [dataTable, auth, provinsi, kabkota],
  );

  const handleEdit = useCallback((record: any) => {
    openWithInitialData(record);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleDelete = useCallback(
    async (record: any) => {
      try {
        const payload = `${record?.nsm}?tahun=${record?.tahun}`;

        const res = await deleteAlokasiBos(payload);

        if (!res) return;

        notifAlert({
          type: "success",
          description: "Penghapusan Dokumen Berhasil",
        });
      } catch (error) {
        console.log(error);
      }
    },
    [deleteAlokasiBos],
  );

  const columns = useMemo(() => {
    const cols = columnsAlokasi({
      provinsi,
      kabkota,
      handleEdit,
      handleDelete,
    });

    if (auth?.kode_role !== "admin_pusat_bos") {
      return cols.filter((_: any, index: number) => index !== 8);
    }

    return cols;
  }, [provinsi, kabkota, handleEdit, handleDelete, auth?.kode_role]);

  const onHandleSave = async (values: any) => {
    const payload: any = {
      nsm: initialData?.nsm,
      nominalBosTahap1: values.tahap1,
      nominalBosTahap2: values.tahap2,
      nominalBosTriwulan1: values.triwulan1,
      nominalBosTriwulan2: values.triwulan2,
      nominalBosTriwulan3: values.triwulan3,
      nominalBosTriwulan4: values.triwulan4,
      nsmMadrasah: initialData?.nsm,
      tahun: initialData?.tahun,
    };
    try {
      const res = await editAlokasiBos(payload);
      if (res) {
        closeModal();
        notifAlert({
          type: "success",
          description: "Data Alokasi Sumber Dana berhasil diubah",
        });
      }
    } catch (error) {
      console.log(error);
    }
  };

  return (
    <>
      <BreadCrumb routes={itemBreadcrumb} title="Alokasi Sumber Dana BOS" />
      <div className="m-5 p-5 bg-white shadow-md rounded">
        <div className="mb-4 flex justify-end">
          <div className="mr-4">
            <Space wrap>
              <ButtonDropdownExport handleExportFile={handleExportFile} />
            </Space>
          </div>
          <InputSearch
            className="w-3/4"
            onChange={(e: any) => setSearch(e.currentTarget.value)}
          />
        </div>
        <TableWithPagination
          loading={loading}
          columns={columns}
          dataTable={dataTable}
          rowKeyField="nsm"
        />
        <ModalAlokasiBos
          dataMadrasah={initialData}
          provinsi={provinsi}
          kabkota={kabkota}
          openModal={open}
          handleSave={onHandleSave}
          handleClose={closeModal}
        />
      </div>
    </>
  );
};

export default AlokasiSumberDanaBos;
